import * as tf from '@tensorflow/tfjs';
import * as blazeface from '@tensorflow-models/blazeface';

// Store models once loaded to avoid reloading
let blazeFaceModel = null;

/**
 * Load TensorFlow.js face detection models
 * @returns {Promise} Promise resolving when models are loaded
 */
export const loadModels = async () => {
  try {
    await tf.ready();
    
    // Set backend to 'webgl' for better performance if available
    await tf.setBackend('webgl');
    console.log('Using TensorFlow.js backend:', tf.getBackend());
    
    // Load BlazeFace model if not already loaded
    if (!blazeFaceModel) {
      console.log('Loading face detection model...');
      blazeFaceModel = await blazeface.load();
      console.log('Face detection model loaded');
    }
    
    return { blazeFaceModel };
  } catch (error) {
    console.error('Error loading models:', error);
    throw new Error('Failed to load face detection models. Please try again.');
  }
};

/**
 * Detect faces in an image
 * @param {string} imageUrl - URL or data URL of the image
 * @returns {Promise<Array>} Promise resolving to array of detected faces
 */
export const detectFaces = async (imageUrl) => {
  try {
    // Load models if not loaded yet
    if (!blazeFaceModel) {
      await loadModels();
    }
    
    // Load the image
    const img = await loadImage(imageUrl);
    
    // Create a tensor from the image
    const tensor = tf.browser.fromPixels(img);
    
    // Detect faces
    const returnTensors = false; // Return raw coordinates instead of tensors
    const predictions = await blazeFaceModel.estimateFaces(tensor, returnTensors);
    
    // Dispose of the tensor to free memory
    tensor.dispose();
    
    return predictions;
  } catch (error) {
    console.error('Error detecting faces:', error);
    throw new Error('Failed to detect faces in the image. Please try again with a clearer photo.');
  }
};

/**
 * Load an image from URL or data URL
 * @param {string} imageUrl - URL or data URL of the image
 * @returns {Promise<HTMLImageElement>} Promise resolving to loaded image element
 */
export const loadImage = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(new Error('Failed to load image'));
    
    img.src = imageUrl;
  });
};

/**
 * Extract facial measurements from BlazeFace detection
 * @param {Array} faces - Array of faces detected by BlazeFace
 * @returns {Object} Object containing facial measurements
 */
export const extractFacialMeasurements = (faces) => {
  if (!faces || faces.length === 0) {
    return null;
  }
  
  // Get the first face
  const face = faces[0];
  
  // BlazeFace provides these landmarks:
  // landmarks[0], landmarks[1]: eyes
  // landmarks[2], landmarks[3]: ears
  // landmarks[4], landmarks[5]: cheeks
  // bottom-most point is probably the nose or mouth
  const landmarks = face.landmarks;
  
  // Get bounding box coordinates
  const topLeft = face.topLeft;
  const bottomRight = face.bottomRight;
  
  // Calculate key points and distances
  const measurements = {
    // Face width (approximated by the distance between ears or bounding box width)
    faceWidth: bottomRight[0] - topLeft[0],
    
    // Face height (from top of bounding box to bottom)
    faceHeight: bottomRight[1] - topLeft[1],
    
    // Jaw width (approximated by the distance between cheek points or lower part of bounding box)
    jawWidth: landmarks[4] && landmarks[5] ? 
              Math.abs(landmarks[4][0] - landmarks[5][0]) : 
              (bottomRight[0] - topLeft[0]) * 0.85,
    
    // Eye width (distance between eyes)
    eyeWidth: landmarks[0] && landmarks[1] ? 
              Math.abs(landmarks[0][0] - landmarks[1][0]) : 
              (bottomRight[0] - topLeft[0]) * 0.4,
    
    // Forehead width (approximated by upper part of bounding box)
    foreheadWidth: (bottomRight[0] - topLeft[0]) * 0.9,
    
    // Cheekbone width (approximated by middle part of bounding box or cheek landmarks)
    cheekboneWidth: landmarks[4] && landmarks[5] ? 
                   Math.abs(landmarks[4][0] - landmarks[5][0]) * 1.1 : 
                   (bottomRight[0] - topLeft[0]) * 0.95,
    
    // Chin position (approximated by bottom point of bounding box)
    chinPosition: [
      topLeft[0] + (bottomRight[0] - topLeft[0]) / 2, // x - center
      bottomRight[1] // y - bottom
    ],
    
    // Bounding box for the face
    boundingBox: {
      topLeft,
      bottomRight
    },
    
    // Face landmarks
    landmarks
  };
  
  return measurements;
};

export default {
  loadModels,
  detectFaces,
  loadImage,
  extractFacialMeasurements
};